import { IDocActivity } from './types'
import {
  ILessonPlan,
  ISimplifiedNode,
} from '../../components/ingestion/IngestedTree'
import { createLessonPlan, createNoteNode } from '../nodeCreationHelpers'
import { PARAGRAPH_SPACING, wrapB } from '../html_utility'
import { flattenChildTablesWhereFirstCellIsHeader } from './utility'

interface ILessonActivityParams {
  metadata: IDocActivity[]
  lessonElement: HTMLElement
  lessonNum: string
  lessonTitle: string
  day: number
  indexOffset: number
}

export function parseLearningPlans({
  lessonElement,
  lessonNum,
  lessonTitle,
  activities,
}: {
  lessonTitle: string
  lessonNum: string
  activities: IDocActivity[]
  lessonElement: HTMLElement
}): ILessonPlan[] {
  const sorted = activities.reduce((acc: IDocActivity[][], activity) => {
    const index = activity.day - 1
    if (!acc[index]) acc[index] = []
    acc[index].push(activity)
    return acc
  }, [])
  let indexOffset = 0
  return sorted.map((set, i) => {
    const { activity, newOffset } = parseLessonDay({
      lessonTitle,
      metadata: set,
      lessonElement,
      lessonNum,
      day: i + 1,
      indexOffset,
    })
    indexOffset = newOffset
    return activity
  })
}

function parseLessonDay(params: ILessonActivityParams) {
  const title = `Lesson ${params.lessonNum}: Day ${params.day}: - ${params.lessonTitle}`
  const nodes = activitiesNodes(params)
  return {
    activity: createLessonPlan(
      {
        title,
      },
      nodes,
    ),
    newOffset: params.indexOffset + nodes.length,
  }
}

function activitiesNodes({
  indexOffset,
  metadata,
  lessonElement,
}: ILessonActivityParams): ISimplifiedNode[] {
  return metadata.map((activity, index) => {
    const activityNode = parseActivity(activity, lessonElement)
    activityNode.data.title = `${index + 1 + indexOffset}: ${activityNode.data.title}`
    return activityNode
  })
}

function parseActivity(
  metadata: IDocActivity,
  lessonElement: HTMLElement,
): ISimplifiedNode {
  const htmlContent = lessonElement.querySelector(metadata['@id'])
  const [mainTable] = Array.from(
    htmlContent?.getElementsByTagName('table') ?? [],
  )

  suggestedPromptFormatting(mainTable)

  const contentString = Array.from(mainTable.rows).reduce<string[]>(
    (acc, row) => {
      switch (row.className) {
        case 'o-ose-activity__subheader': {
          const innerp = row.querySelector('p')
          acc.push(innerp!.outerHTML.replace('MATERIALS', wrapB('MATERIALS')))
          break
        }
        case 'o-ose-activity__body': {
          // This is all the main content, go through each child
          // if it's a table flatten it
          const firstCell = row.cells.item(0)
          const secondCell = row.cells.item(1)
          if (firstCell) {
            const content = flattenChildTablesWhereFirstCellIsHeader(firstCell)
            content.trim() && acc.push(content)
          }
          if (secondCell) {
            const childrenContent: string[] = []

            Array.from(secondCell.childNodes).forEach((child) => {
              if ((child as HTMLElement).tagName === 'H4') {
                let newChild =
                  '<div class="teacher-facing-callout">' +
                  (child as HTMLElement).outerHTML
                let nextSibling = (child as HTMLElement).nextElementSibling
                const siblingContent = []
                while (nextSibling && nextSibling?.tagName !== 'H4') {
                  if (
                    nextSibling.className != 'do-not-strip' &&
                    nextSibling.outerHTML !== '<p></p>'
                  ) {
                    siblingContent.push(nextSibling.outerHTML)
                  }
                  nextSibling = nextSibling.nextElementSibling
                }
                newChild += `${siblingContent.join(PARAGRAPH_SPACING)}</div>`
                childrenContent.push(newChild)
              }
            })
            childrenContent.length &&
              acc.push(childrenContent.join(PARAGRAPH_SPACING))
          }
          break
        }
      }
      return acc
    },
    [],
  )

  return createNoteNode({
    title: metadata.name,
    value: contentString
      .join(PARAGRAPH_SPACING)
      .replaceAll('<p></p>', PARAGRAPH_SPACING),
    studentFacing: false,
  })
}

function suggestedPromptFormatting(contentElement: Element) {
  contentElement
    .querySelectorAll<HTMLTableElement>('table.o-ose-prompt')
    .forEach((table) => {
      evenColumnWidth(table)
      contentItalicize(table)
    })
}

function contentItalicize(table: HTMLTableElement) {
  Array.from(table.rows).forEach((row) => {
    if (!row.rowIndex) return
    Array.from(row.cells).forEach((cell) => {
      Array.from(cell.getElementsByTagName('span')).forEach((span) => {
        if (cell.cellIndex % 2) {
          span.removeAttribute('style')
        } else {
          span.style.fontStyle = 'italic'
        }
      })
    })
  })
}

function evenColumnWidth(table: HTMLTableElement) {
  const cells = table.rows.item(0)?.cells
  if (!cells?.length) return
  const width = 100 / cells.length
  Array.from(cells).forEach((cell) => {
    cell.style.width = `${width}%`
  })
}
